// /js/tiptap/ChartSection.js

import { forEach } from '@tiptap/core'
import { Node } from '@tiptap/core'

/*
  ChartSection
  -------------
  Represents a single logical section of a medical chart
  (HPI, ROS, PE, MDM, Plan, etc.)

  This node is:
  - a block-level container
  - editable (not atomic)
  - allowed only inside chart_template
  - visually styled via CSS (not here)
*/

const SECTION_LABELS = {
  cc: 'Chief Complaint',
  hpi: 'HPI',
  ros: 'ROS',
  pmh: 'Past Medical History',
  psh: 'Surgical History',
  fh: 'Family History',
  sh: 'Social History',
  quickphrase: "quickphrase", 
  gyn: 'Gynecologic History',
  meds: 'Medications',
  allergies: 'Allergies',
  immunizations: 'Immunizations',
  pe: 'Physical Exam',
  procedure: 'Procedures',
  mdm: 'Medical Decision Making',
  a_p: 'Assessment & Plan',
  impression: 'Clinical Impression',
  disposition: 'Disposition',
  labs: 'Lab Orders',
  addendum: 'Addendum',

	subjective: 'Subjective',
	objective: 'Objective',
	assessment: 'Assessment',
	plan: 'Plan',

  // authored clinical documents. A section_type with no entry here renders as the literal 'Section',
  // so every type a template can carry needs a label before that template is offered to a provider.
  requested_study: 'Requested study',
  diagnosis_icd: 'Diagnosis',
  indication: 'Indication',
  conservative_therapy: 'Conservative therapy tried and failed',
  relevant_findings: 'Relevant examination findings',
  prior_imaging: 'Prior imaging and results',
  expected_impact: 'What this study will change',

  // letter to a clinician. 'relevant_findings' and 'diagnosis_icd' above are shared with the necessity
  // summary and are deliberately not duplicated here.
  letter_reason: 'Reason for writing',
  history_course: 'History and course',
  meds_allergies: 'Current medications and allergies',
  investigations: 'Investigations and results',
  management_tried: 'Management to date and response',
  clinical_question: 'What is being asked',
  insurance: 'Insurance',
}

export const ChartSection = Node.create({
  // Unique node name used in the schema and JSON
  name: 'chart_section',

  // This node behaves like a block element (like a div)
  group: 'block',

  // What this section is allowed to contain:
  // one or more block-level nodes
  // (paragraphs, lists, tables, atomic embeds, etc.)
  content: 'block+',

  // Prevents content outside this section from merging into it
  // (important for structured documents like medical charts)
  isolating: true,

  // Makes this node a stable structural boundary
  // ProseMirror prefers keeping it intact during edits
  defining: true,

  selectable: true, // 🔑 REQUIRED

  /*
    Attributes stored on the section itself.
    These are serialized into the document JSON.

    section_type controls:
    - labeling (HPI, ROS, PE…)
    - styling (CSS)
    - future logic (locking, hashing, signatures)
  */
  addAttributes() {
    return {
      section_type: {
        default: 'hpi', // History of Present Illness
      },
    }
  },

  addAttributes() {
    return {
        section_type: { default: 'hpi' },

        // NEW: optional visibility condition
        visible_if: {
        default: null, // e.g. { field: 'patient_gender', equals: 'female' }
        },
    }
},

  /*
    Defines how this node is recognized when parsing HTML
    back into a TipTap document.

    <chart-section data-section="hpi">...</chart-section>
  */
  parseHTML() {
    return [{ tag: 'chart-section' }]
  },

  /*
    Defines how this node is rendered into HTML.

    - chart-section is a custom HTML tag
    - data-section attribute exposes section_type for CSS / JS
    - the final "0" is where child content is injected
  */
  renderHTML({ node }) {
    return [
      'chart-section',
      { 'data-section': node.attrs.section_type },
      0
    ]
  },

  // 👇 THIS IS THE NEW PART
  addNodeView() {
    return ({ node, editor, getPos }) => {
      // outer wrapper (matches chart-section tag)
      const dom = document.createElement('chart-section')
      dom.setAttribute('data-section', node.attrs.section_type)

			

      // header (non-editable)
      // Only render header if NOT quickphrase
			if (node.attrs.section_type !== 'quickphrase') 
			{
				const header = document.createElement('div')
				header.className = 'chart_section_header'
				header.textContent =SECTION_LABELS[node.attrs.section_type] || 'Section'
				dom.appendChild(header)
			}

      // content container (editable area)
      const contentDOM = document.createElement('div')
      contentDOM.className = 'chart_section_content'
      dom.appendChild(contentDOM)

      return {
        dom,
        contentDOM,
      }
    }
  },
})
